import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { snackActions } from '@/components/snackbarUtils/SnackbarUtils'
import {
  User,
  UserResponse,
  UsersResponse,
  FullUserResponse,
  UserCreate,
  UserUpdate,
} from '@backend/users/usersRouter'
import { SearchParam } from '@backend/types'
import { IId, ISchemaAndId } from '../../types/common'
import { prepareHeaders, handleApiError } from './helpers'

export enum ERole {
  User = 'USER',
  SchemaCreator = 'SCHEMA_CREATOR',
  Attendancer = 'ATTENDANCER',
  Admin = 'ADMIN',
}

export interface IUserObject extends User {}

export interface IUser extends UserResponse {}

export interface IUserCreate extends UserCreate {}

export interface IUserUpdate extends UserUpdate {}

export interface IFullUser extends FullUserResponse {}

export const usersApi = createApi({
  reducerPath: 'users',
  baseQuery: fetchBaseQuery({
    baseUrl: window.env.API_ENDPOINT,
    prepareHeaders,
  }),
  endpoints: (builder) => ({
    getUsers: builder.query<UsersResponse, SearchParam>({
      query: ({ limit, offset, sort, sortKey, key, value }) => ({
        url: 'users/',
        params: { limit, offset, sort, sortKey, key, value },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.users.errors.getUsers', err)
        }
      },
    }),
    getUser: builder.query<FullUserResponse, IId>({
      query: ({ id }) => ({
        url: `users/${id}`,
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          const result = await queryFulfilled
          localStorage.setItem(
            'schemaIds',
            JSON.stringify(result.data.schemas.map((schema) => schema.id)),
          )
        } catch (err) {
          handleApiError('api.users.errors.getUser', err)
        }
      },
    }),
    addUser: builder.query<void, IUserCreate>({
      query: ({ email, firstName, role, lastName, phoneNumber }) => ({
        url: 'users/',
        method: 'POST',
        body: {
          email: email.trim(),
          firstName: firstName.trim(),
          role,
          lastName: lastName.trim(),
          phoneNumber: phoneNumber.trim(),
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.users.errors.addUser', err)
        }
      },
    }),
    resendInventationMail: builder.query<void, IId>({
      query: ({ id }) => ({
        url: `users/${id}/resend-invitation-mail`,
        method: 'POST',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
          snackActions.success('api.users.success.resendInventationMail')
        } catch (err) {
          handleApiError('api.users.errors.resendInventationMail', err)
        }
      },
    }),
    submitTimeslots: builder.query<void, ISchemaAndId>({
      query: ({ id, schemaId }) => ({
        url: `users/schema/${schemaId}/${id}/submit-timeslots`,
        method: 'POST',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.users.errors.submitTimeslots', err)
        }
      },
    }),
    updateUser: builder.query<void, IUserUpdate & IId>({
      query: ({ id, email, firstName, role, lastName, phoneNumber }) => ({
        url: `users/${id}`,
        method: 'PATCH',
        body: {
          email: email.trim(),
          firstName: firstName.trim(),
          role,
          lastName: lastName.trim(),
          phoneNumber: phoneNumber?.trim(),
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.users.errors.updateUser', err)
        }
      },
    }),
    deleteUser: builder.query<void, IId>({
      query: ({ id }) => ({
        url: `users/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.users.errors.deleteUser', err)
        }
      },
    }),
  }),
})

export const {
  useLazyGetUsersQuery,
  useLazyGetUserQuery,
  useLazyAddUserQuery,
  useLazyUpdateUserQuery,
  useLazyResendInventationMailQuery,
  useLazySubmitTimeslotsQuery,
  useLazyDeleteUserQuery,
} = usersApi
